import { Component, computed, inject, input, ChangeDetectionStrategy } from "@angular/core";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { TableModule } from "@openng/optimus-ui/table";
import { SvgIconComponent } from "../../components/svg-icon/svg-icon.component";
import { DialogService, DynamicDialogRef } from "@openng/optimus-ui/dynamicdialog";
import { FixLinkComponent } from "../../components/fix-link/fix-link.component";
import { RepairStatusPipe } from "../../pipes/repairStatus/repair-status.pipe";
import { ProgressSpinnerModule } from "@openng/optimus-ui/progressspinner";
import { MessageService } from "@openng/optimus-ui/api";
import { TooltipModule } from "@openng/optimus-ui/tooltip";
import { GateIdentifierPipe } from "../../pipes/gateIdentifier/gate-identifier.pipe";
import { HasPermissionDirective } from "../../directives/has-permission/has-permission.directive";
import { PermissionService } from "../../../services/api/permission/permission.service";
import { createTimeoutSignal } from "../../utils/timeout.utils";
import { TimeoutComponent } from "../../components/timeout/timeout.component";
import { GateFault } from "../../../models/api/fault/gateFault";
import { Column } from "../../../models/ui/tableColumn";
import { REPAIR_STATUS } from "../../../enums/repairStatus.enum";

@Component({
  standalone: true,
  selector: "app-gate-fault-table",
  imports: [
    TableModule,
    TranslateModule,
    SvgIconComponent,
    RepairStatusPipe,
    ProgressSpinnerModule,
    TooltipModule,
    HasPermissionDirective,
    TimeoutComponent,
  ],
  templateUrl: "./gate-fault-table.component.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: "./gate-fault-table.component.scss",
})
export class GateFaultTableComponent {
  public readonly gateFaults = input<GateFault[] | null>();

  protected readonly displayedGateFaults = computed(() => {
    const gateFaults: GateFault[] = this.gateFaults() ?? [];
    return gateFaults.filter((fault: GateFault) => fault.faultType);
  });
  protected readonly loading = computed(() => this.gateFaults() === null);
  protected readonly timeOut = createTimeoutSignal(this.loading);

  protected readonly gateIdentifierPipe = inject(GateIdentifierPipe);

  private readonly translate = inject(TranslateService);
  private readonly dialogService = inject(DialogService);
  protected readonly permissions = inject(PermissionService);
  protected readonly repairStatuses = REPAIR_STATUS;
  private readonly message = inject(MessageService);

  private ref: DynamicDialogRef | null = null;
  protected readonly GATE_FAULTS_COLUMNS: Column[] = [
    {
      field: "gateSystem",
      header: "gate_faults.table.gate_system",
    },
    {
      field: "faultText",
      header: "gate_faults.table.fault_text",
    },
    {
      field: "repairStatus",
      header: "gate_faults.table.repair_status",
    },
  ];

  /**
   * Opens the fault solution as repair-capable or read-only details.
   */
  public openDetails(fault: GateFault, dialogType: "request" | "details"): void {
    this.ref = this.dialogService.open(FixLinkComponent, {
      header: this.translate.instant(`gate_faults.repair_modal.title.${dialogType}`),
      modal: true,
      width: "50vw",
      contentStyle: { overflow: "auto" },
      breakpoints: {
        "960px": "75vw",
        "640px": "90vw",
      },
      data: {
        fixLink: fault.fixLink,
        repairStatus: fault.repairStatus,
        gateFaultId: fault.id,
      },
      closable: true,
    });

    this.ref?.onClose.subscribe({
      next: (res) => {
        if (res) {
          switch (res) {
            case "request_success":
              this.message.add({
                key: "global",
                severity: "success",
                summary: this.translate.instant("common.success"),
                detail: this.translate.instant("gate_faults.repair_modal.request_success"),
                life: 5000,
              });
              break;
            case "request_error":
              this.message.add({
                key: "global",
                severity: "error",
                summary: this.translate.instant("common.error"),
                detail: this.translate.instant("gate_faults.repair_modal.request_error"),
                life: 5000,
              });
          }
        }
      },
    });
  }

  public getDialogType(fault: GateFault): "request" | "details" {
    if (
      this.permissions.hasPermission(this.permissions.allPermissions.REPAIR_REQUEST_CREATE) &&
      (!fault.repairStatus ||
        fault.repairStatus === REPAIR_STATUS.None ||
        fault.repairStatus === REPAIR_STATUS.Archived)
    ) {
      return "request";
    }

    return "details";
  }
}
